import { motion } from "framer-motion";
import { Activity, Brain, Network, Cpu, Gauge } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";

function pct(n) { return `${Math.round(n * 100)}%`; }

export function GenomePanel({ genome }) {
  if (!genome) return null;
  const { memory_state, prediction_error, cycles, concepts = [], insights = [], doctrine } = genome;
  const designated = insights.filter((i) => i.designated);

  return (
    <div className="tracing-beam rounded-lg" data-testid="genome-panel">
      <div className="tracing-beam-inner rounded-lg" />
      <div className="relative p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-acid" />
            <span className="font-heading font-bold tracking-tight">HNHB Genome</span>
          </div>
          <span className="mono-label text-acid">{memory_state}</span>
        </div>

        <div className="grid grid-cols-3 gap-px bg-[#1E222A]">
          <Metric icon={Cpu} label="Concepts" value={concepts.length} />
          <Metric icon={Network} label="Insights" value={insights.length} />
          <Metric icon={Activity} label="Cycles" value={cycles} />
        </div>

        <div className="surface rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="mono-label flex items-center gap-1"><Gauge className="w-3 h-3" /> Prediction-Error Pulse</span>
            <span className="font-mono2 text-sm" style={{ color: prediction_error > 0.55 ? "#FF3B30" : "#CCFF00" }}>
              {prediction_error.toFixed(4)}
            </span>
          </div>
          <div className="h-1.5 bg-[#05060a] rounded-full overflow-hidden">
            <motion.div className="h-full rounded-full"
              style={{ background: prediction_error > 0.55 ? "#FF3B30" : "#CCFF00" }}
              initial={{ width: 0 }} animate={{ width: `${prediction_error * 100}%` }} transition={{ duration: 0.8 }} />
          </div>
        </div>

        <div>
          <p className="mono-label mb-2">Hebbian Concepts</p>
          <div className="space-y-1.5">
            {concepts.slice(0, 6).map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3" data-testid={`concept-row-${i}`}>
                <span className="text-xs text-[#8A91A6] font-mono2 w-6">{pct(c.weight)}</span>
                <div className="flex-1 h-1 bg-[#05060a] rounded-full overflow-hidden">
                  <div className="h-full bg-neural" style={{ width: `${c.weight * 100}%` }} />
                </div>
                <span className="text-xs text-white/90 truncate max-w-[46%]">{c.display}</span>
              </motion.div>
            ))}
            {concepts.length === 0 && <p className="text-xs text-[#565C6F]">No concepts encoded yet.</p>}
          </div>
        </div>

        <div>
          <p className="mono-label mb-2">Designated Bayesian Insights</p>
          <div className="space-y-2">
            {designated.slice(0, 4).map((ins, i) => (
              <motion.div key={ins.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.06 }}
                className="surface rounded-md p-3" data-testid={`insight-row-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-white/90 leading-snug">{ins.statement}</p>
                  <span className="font-mono2 text-sm shrink-0" style={{ color: "#FFB340" }}>{pct(ins.confidence)}</span>
                </div>
                {ins.history?.length > 1 && (
                  <div className="h-8 mt-2 -mx-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={ins.history}>
                        <YAxis domain={[0, 1]} hide />
                        <Tooltip contentStyle={{ background: "#0F1115", border: "1px solid #1E222A", fontSize: 11 }}
                          formatter={(v) => pct(v)} labelFormatter={() => ""} />
                        <Line type="monotone" dataKey="confidence" stroke="#FFB340" strokeWidth={1.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </motion.div>
            ))}
            {designated.length === 0 && (
              <p className="text-xs text-[#565C6F]">OCTON has not designated any insights yet - log a session or run a sleep cycle.</p>
            )}
          </div>
        </div>

        <p className="mono-label text-[#565C6F] leading-relaxed pt-1 border-t border-[#1E222A]">{doctrine}</p>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#0F1115] p-3 flex flex-col items-center gap-1">
      <Icon className="w-4 h-4 text-[#565C6F]" />
      <span className="font-heading text-2xl font-bold">{value}</span>
      <span className="mono-label tracking-[0.12em] text-[0.6rem]">{label}</span>
    </div>
  );
}
